import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { forkJoin, Subscription, timer } from 'rxjs';

import { OcJsonSchemaValidatorAjv } from '../core/oc-validator/oc-jsonSchemaValidatorAjv.service';
import { IError } from '@orgacard/login';
import moment from 'moment';
import { ok } from 'node:assert';

import { AppService } from '../app.service';
import { CoreService } from '../core/core.service';
import { EnvironmentService } from '../core/environment.service';
import { UtilityHelperService } from '../core/utility-helper.service';
import { LoadingOverlayService } from '../loading-overlay/loading-overlay.service';
import { ICountry } from '../mwsp/basedata/general/countries/country.model';
import { IVariant } from '../mwsp/shared/models/basic.model';
import { ISystemSetupModel } from './systemconfig.model';
import { ISystemconfigResponse, SystemconfigService } from './systemconfig.service';

@Component({
    selector: 'app-systemconfig',
    templateUrl: './systemconfig.component.html',
    standalone: false,
})
export class SystemconfigComponent implements OnInit, OnDestroy {
    private readonly utilityHelperService = inject(UtilityHelperService);
    private readonly router = inject(Router);
    private readonly appService = inject(AppService);
    readonly environmentService = inject(EnvironmentService);
    private readonly formBuilder = inject(UntypedFormBuilder);
    readonly systemconfigService = inject(SystemconfigService);
    readonly coreService = inject(CoreService);
    readonly loadingOverlayService = inject(LoadingOverlayService);
    readonly ocJsonSchemaValidatorAjv = inject(OcJsonSchemaValidatorAjv);

    public step: number = 1;

    public msgDbLoading: string = '';
    public msgDbLoadingStart: string = '';

    public ticks = 0;
    public isLoading: boolean = false;

    public systemSetupForm: UntypedFormGroup;
    public passwordOcAdminType: boolean;
    public passwordAdminType: boolean;
    public variantsLB: IVariant[] = [];
    public countryLB: ICountry[] = [];
    public systemSetup: ISystemSetupModel;
    protected systemconfigResponse: ISystemconfigResponse;

    private timer = timer(2000, 10000);
    // Subscription object
    private timerSub: Subscription;

    ngOnInit(): void {
        this.loadingOverlayService.startLoading();
        this.initForm();
        this.systemSetup = {} as ISystemSetupModel;
        //
        this.systemconfigService.getMandatorCount().subscribe({
            next: (ok) => {
                if (this.systemconfigService.mandatorCount > 0) {
                    this.initData();
                }
                this.loadingOverlayService.stopLoading();
            },
            error: (err: IError) => {
                this.utilityHelperService.handleComponentError(err);
            },
        });
    }

    ngOnDestroy() {
        // console.log('Destroy timer');
        if (this.timerSub && !this.timerSub.closed) {
            // unsubscribe here
            this.timerSub.unsubscribe();
            this.stopTimer();
        }
    }

    startLoading() {
        this.systemconfigService.startDatabaseLoading().subscribe({
            next: (ok) => {
                this.isLoading = true;
                if (!this.timerSub || this.timerSub.closed) {
                    this.startTimer();
                }
            },
            error: (err: IError) => {
                this.utilityHelperService.handleComponentError(err);
            },
        });
    }

    getLoadingState() {
        this.systemconfigService.getLoadingState().subscribe({
            next: (ok) => {
                if (ok) {
                    this.appService.showToast(
                        'success',
                        this.coreService.getMsgkey('prompt.note', undefined),
                        this.coreService.getMsgkey('mws.systemconfiguration.step1.dbready', undefined),
                    );
                    this.stopTimer();
                    this.initData();
                } else {
                    this.msgDbLoading = this.coreService.getMsgkey('mws.systemconfiguration.step1.dbloading', [
                        this.msgDbLoadingStart,
                        moment(new Date()).locale(this.environmentService.appSettings.locale).format('LTS').toString(),
                    ]);
                    this.appService.showToast('info', this.coreService.getMsgkey('prompt.note', undefined), this.msgDbLoading);
                }
            },
            error: (err: IError) => {
                this.utilityHelperService.handleComponentError(err);
            },
        });
    }

    startTimer() {
        if (!this.timerSub || this.timerSub.closed) {
            this.appService.showToast(
                'info',
                this.coreService.getMsgkey('prompt.note', undefined),
                this.coreService.getMsgkey('mws.systemconfiguration.step1.startloading', undefined),
            );
            this.timerSub = this.timer.subscribe((t) => this.tickerFunc(t));
            this.msgDbLoadingStart = moment(new Date()).locale(this.environmentService.appSettings.locale).format('LTS').toString();
        }
    }

    stopTimer() {
        // unsubscribe here
        if (this.timerSub && !this.timerSub.closed) {
            this.timerSub.unsubscribe();
            this.timerSub = undefined;
        }
    }

    tickerFunc(tick: any) {
        this.ticks = tick;
        this.getLoadingState();
    }

    initData() {
        this.getListValues();
    }

    initForm() {
        this.initFormValues(); // IMMER MACHEN sonst steht FormGroup nicht rechtzeitig zu Verfügung
        this.systemSetup = {} as ISystemSetupModel;
    }

    initFormValues() {
        this.systemSetupForm = this.formBuilder.group({
            name: new UntypedFormControl(
                {
                    value: '',
                    disabled: false,
                },
                Validators.required,
            ),
            countryId: new UntypedFormControl(
                {
                    value: null,
                    disabled: false,
                },
                Validators.required,
            ),
            currencyId: new UntypedFormControl(
                {
                    value: null,
                    disabled: false,
                },
                Validators.required,
            ),
            languageId: new UntypedFormControl(
                {
                    value: undefined,
                    disabled: false,
                },
                Validators.required,
            ),
            passwordOcAdmin: new UntypedFormControl(
                {
                    value: '',
                    disabled: false,
                },
                Validators.required,
            ),
            passwordAdmin: new UntypedFormControl(
                {
                    value: '',
                    disabled: false,
                },
                Validators.required,
            ),
            changed: false,
        });
        this.systemSetupForm.valueChanges.subscribe((change) => {
            if (!this.systemSetupForm.get('changed').touched && this.systemSetupForm.valid) {
                this.systemSetupForm.get('changed').markAsTouched();
            } else if (!this.systemSetupForm.valid) {
                this.systemSetupForm.get('changed').markAsUntouched();
            }
        });
    }

    saveSystemSetupData() {
        this.loadingOverlayService.startLoading();
        if (this.systemSetupForm.valid) {
            this.mapFormDataToSystemSetupObject();
            //
            this.systemconfigService.save(this.systemSetup).subscribe({
                next: (systemconfigResponse: ISystemconfigResponse) => {
                    this.loadingOverlayService.stopLoading();
                    this.systemconfigResponse = systemconfigResponse;
                    this.nextStep();
                },
                error: (err: IError) => {
                    this.utilityHelperService.handleComponentError(err);
                },
            });
        }
    }

    mapFormDataToSystemSetupObject() {
        this.systemSetup.name = this.systemSetupForm.get('name').value;
        this.systemSetup.countryId = this.systemSetupForm.get('countryId').value;
        this.systemSetup.currencyId = this.systemSetupForm.get('currencyId').value;
        this.systemSetup.languageId = this.systemSetupForm.get('languageId').value;
        this.systemSetup.passwordOcAdmin = this.systemSetupForm.get('passwordOcAdmin').value;
        this.systemSetup.passwordAdmin = this.systemSetupForm.get('passwordAdmin').value;
    }

    getListValues() {
        this.loadingOverlayService.startLoading();
        const observableBatch = [];
        observableBatch.push(this.coreService.getCurrencies());
        observableBatch.push(this.coreService.getCountries());
        observableBatch.push(this.coreService.getLanguages());
        forkJoin(observableBatch).subscribe({
            next: (results) => {
                this.environmentService.countryList.forEach((c) => {
                    c.countrylanguages.forEach((l) => {
                        // console.log('systemlogin.component.ts: VARIANT: ' + l.language.languageIso639 + '_' + c.countryIso3166);
                        const variant: IVariant = {
                            variant: l.language.languageIso639 + '_' + c.countryIso3166,
                            //description: l.language.descr + ' - ' + c.descr,
                            description: c.descr,
                            styleClassCountry: c.styleclass,
                            styleClassLanguage: l.language.styleClass,
                        };
                        this.environmentService.variantList.push(variant);
                        if (this.environmentService.variantHash.get(l.language.languageIso639)) {
                            this.environmentService.variantHash.get(l.language.languageIso639).push(c);
                        } else {
                            const countries = [c];
                            this.environmentService.variantHash.set(l.language.languageIso639, countries);
                        }
                    });
                });
                // START init DEFAULT-Vorbelegung
                const languageId = this.environmentService.languageHash.get(this.environmentService.systemUser.language).id;
                this.systemSetupForm.get('languageId').setValue(languageId);
                //
                this.nextStep();
                this.loadingOverlayService.stopLoading();
            },
            error: (err: IError) => {
                // TODO
                // this.loadingOverlayService.stopLoading();
                let errMsg = '';
                if (err.error && err.error.msgkey) {
                    errMsg = this.coreService.getMsgkey(err.error.msgkey, err.error.msgargs);
                } else {
                    errMsg = err.statusText;
                }
                this.appService.showToast(
                    'error',
                    this.coreService.getMsgkey('prompt.error', []) + ': ' + (err.status ? err.status : ''),
                    'loginComponent ngOnInit forkJoin ' + errMsg,
                );
                this.logout();
            },
        });
    }

    logout() {
        localStorage.removeItem('loggedInUser');
        this.router.navigate(['/']);
    }

    nextStep() {
        this.step++;
    }

    // previousStep() {
    //     this.step--;
    // }

    togglePasswordOcAdminType() {
        this.passwordOcAdminType = !this.passwordOcAdminType;
    }

    togglePasswordAdminType() {
        this.passwordAdminType = !this.passwordAdminType;
    }
}
